import { Box, Image, Text, AspectRatio } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import Rating from "../UI/Rating";
import { useRef, useState } from "react";
import placeholderThumbnail from "../../assets/GameThumbnail_Empty.png";
import placeholderFace from "../../assets/GameThumbnail_Empty.png";

//const MEDIA_RATIO = 4 / 3; //16:9 ratio
const MEDIA_RATIO = 1; // square
const MIN_PLACEHOLDER_MS = 120;

function GameItem({
  title,
  thumbnail,
  id,
  rating,
  onHover,
  gameData,
  isPC,
  vidPath,
}) {
  const itemRef = useRef(null);

  // desktop and mobile hover
  const [isHovered, setIsHovered] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const hoverStartRef = useRef(0);
  const switchTimerRef = useRef(null);

  // mobile long-press preview (kept from your version)
  const [showVideo, setShowVideo] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const [holdTimeout, setHoldTimeout] = useState(null);
  const [releaseTimeout, setReleaseTimeout] = useState(null);

  const mediaTileProps = {
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "sm",
    bg: "gray.100",
    transition: "transform 120ms ease, box-shadow 120ms ease",
    _hover: { transform: "translateY(-2px)", boxShadow: "md" },
  };

  // -------------------- DESKTOP --------------------
  if (isPC) {
    const hasInlinePreview = Boolean(vidPath); // play inline if we have a video

    const handleMouseEnter = () => {
      setIsHovered(true);
      setVideoReady(false);
      hoverStartRef.current = performance.now();
      // IMPORTANT: Do NOT call onHover here; this disables the popup everywhere
      // If you want popup fallback when there is no video, uncomment below:
      // if (!hasInlinePreview && onHover) onHover({ game: gameData || { title, thumbnail, id, rating }, elementRef: itemRef, show: true });
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      setVideoReady(false);
      // if (onHover) onHover({ show: false });
      if (switchTimerRef.current) {
        clearTimeout(switchTimerRef.current);
        switchTimerRef.current = null;
      }
    };

    return (
      <Box
        ref={itemRef}
        as={Link}
        to={`/games/${id}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        _hover={{ textDecoration: "none" }}
      >
        <Box {...mediaTileProps}>
          <AspectRatio ratio={MEDIA_RATIO}>
            {hasInlinePreview && isHovered ? (
              <video
                autoPlay
                loop
                muted
                playsInline
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                //style={{ width: "100%", height: "100%", objectFit: "contain" }}
              >
                <source src={vidPath} type="video/mp4" />
              </video>
            ) : (
              <Image
                onLoad={(e) => (e.currentTarget.src = thumbnail)}
                alt={title}
                src={placeholderThumbnail}
                loading="lazy"
                objectFit="cover"
                //objectFit="contain"
              />
            )}
          </AspectRatio>
        </Box>

        <Text
          mt={2}
          fontSize="md"
          fontWeight="semibold"
          color="gray.700"
          noOfLines={2}
        >
          {title}
        </Text>

        <Box display="none">
          <Rating justifyContent="flex-start" rating={rating} />
        </Box>
      </Box>
    );
  }

  // -------------------- MOBILE (unchanged behavior) --------------------
  const resetVideoState = () => {
    if (holdTimeout) clearTimeout(holdTimeout);
    if (releaseTimeout) clearTimeout(releaseTimeout);
    setHoldTimeout(null);
    setReleaseTimeout(null);
    setIsHolding(false);
    setShowVideo(false);
    // if (onHover) onHover({ show: false });
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    const timeout = setTimeout(() => {
      setIsHolding(true);
      setShowVideo(true);
      // if (onHover) onHover({ game: gameData || { title, thumbnail, id, rating }, elementRef: itemRef, show: true });
    }, 500);
    setHoldTimeout(timeout);
  };

  const handleTouchEnd = (e) => {
    if (isHolding) {
      e.preventDefault();
      const timeout = setTimeout(() => resetVideoState(), 500);
      setReleaseTimeout(timeout);
    } else {
      resetVideoState();
    }
  };

  const handleTouchCancel = resetVideoState;

  const handleTouchMove = (e) => {
    if (itemRef.current && e.touches.length > 0 && (holdTimeout || isHolding)) {
      const t = e.touches[0];
      const r = itemRef.current.getBoundingClientRect();
      const inside =
        t.clientX >= r.left &&
        t.clientX <= r.right &&
        t.clientY >= r.top &&
        t.clientY <= r.bottom;
      if (!inside) {
        if (isHolding && !releaseTimeout)
          setReleaseTimeout(setTimeout(resetVideoState, 500));
      } else if (releaseTimeout) {
        clearTimeout(releaseTimeout);
        setReleaseTimeout(null);
      }
    }
  };

  return (
    <Box
      ref={itemRef}
      as={Link}
      to={`/games/${id}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      onTouchMove={handleTouchMove}
      _hover={{ textDecoration: "none" }}
    >
      <Box {...mediaTileProps}>
        <AspectRatio ratio={MEDIA_RATIO}>
          {showVideo && vidPath ? (
            <video
              autoPlay
              loop
              muted
              playsInline
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            >
              <source src={vidPath} type="video/mp4" />
            </video>
          ) : (
            <Image
              onLoad={(e) => (e.currentTarget.src = thumbnail)}
              alt={title}
              src={placeholderThumbnail}
              loading="lazy"
              objectFit="contain"
            />
          )}
        </AspectRatio>
      </Box>

      <Text
        mt={2}
        fontSize="md"
        fontWeight="semibold"
        color="gray.700"
        noOfLines={2}
      >
        {title}
      </Text>

      <Box display="none">
        <Rating justifyContent="flex-start" rating={rating} />
      </Box>
    </Box>
  );
}

export default GameItem;
